/**
 * CellEffectsSystem - Visual effects for life functions
 * Creates particle effects, animations, and visual feedback for cell processes
 */

import Phaser from 'phaser';
import { StimulusType } from '@/game/systems/LifeFunctionSystem';

/**
 * Configuration for the effects system
 */
export interface CellEffectsConfig {
  /** Whether to use particles for metabolism */
  useParticles?: boolean;
  /** Intensity multiplier for effects */
  intensity?: number;
  /** Whether to reduce motion for accessibility */
  reduceMotion?: boolean;
}

const DEFAULT_CONFIG: Required<CellEffectsConfig> = {
  useParticles: true,
  intensity: 1.0,
  reduceMotion: false,
};

/**
 * Division phase data for animation
 */
interface DivisionAnimation {
  tweenChain: Phaser.Tweens.TweenChain;
  phase: string;
  container: Phaser.GameObjects.Container;
}

/**
 * CellEffectsSystem manages visual effects for life functions
 */
export class CellEffectsSystem {
  private scene: Phaser.Scene;
  private config: Required<CellEffectsConfig>;
  private activeEffects: Map<string, Phaser.GameObjects.GameObject[]> = new Map();
  private divisionAnimations: Map<string, DivisionAnimation> = new Map();
  private particleEmitters: Map<string, Phaser.GameObjects.Particles.ParticleEmitter> = new Map();

  constructor(scene: Phaser.Scene, config: CellEffectsConfig = {}) {
    this.scene = scene;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // ============================================================================
  // METABOLISM EFFECTS
  // ============================================================================

  /**
   * Visualize metabolism with energy particles from mitochondria
   */
  visualizeMetabolism(
    container: Phaser.GameObjects.Container,
    cellId: string,
    duration: number = 2000
  ): void {
    if (this.config.reduceMotion) {
      this.flashEffect(container, 0xed8936, 500);
      return;
    }

    const effects: Phaser.GameObjects.GameObject[] = [];
    const particleCount = Math.floor(8 * this.config.intensity);

    // Create ATP-like particles emanating from center
    for (let i = 0; i < particleCount; i++) {
      const particle = this.scene.add.graphics();
      particle.fillStyle(0xf6e05e, 0.8); // Yellow-gold for ATP
      particle.fillCircle(0, 0, 3 + Math.random() * 2);

      // Add glow
      particle.lineStyle(2, 0xed8936, 0.5);
      particle.strokeCircle(0, 0, 5 + Math.random() * 2);

      const startX = (Math.random() - 0.5) * 30;
      const startY = (Math.random() - 0.5) * 30;
      particle.setPosition(startX, startY);
      particle.setAlpha(0);

      container.add(particle);
      effects.push(particle);

      // Animate particle burst
      this.scene.tweens.add({
        targets: particle,
        alpha: 1,
        delay: i * 100,
        duration: 200,
        yoyo: true,
        hold: 300,
        onComplete: () => {
          // Drift outward
          this.scene.tweens.add({
            targets: particle,
            x: startX * 2.5,
            y: startY * 2.5,
            alpha: 0,
            duration: 500,
            onComplete: () => particle.destroy()
          });
        }
      });
    }

    // Pulse effect on the cell
    this.scene.tweens.add({
      targets: container,
      scaleX: container.scaleX * 1.05,
      scaleY: container.scaleY * 1.05,
      duration: 300,
      yoyo: true,
      repeat: 2,
      ease: 'Sine.easeInOut'
    });

    this.activeEffects.set(`${cellId}_metabolism`, effects);

    // Clean up after duration
    this.scene.time.delayedCall(duration, () => {
      this.cleanupEffects(`${cellId}_metabolism`);
    });
  }

  // ============================================================================
  // CELL DIVISION EFFECTS
  // ============================================================================

  /**
   * Visualize cell division with a multi-phase animation
   * Returns a tween chain that can be controlled
   */
  visualizeCellDivision(
    container: Phaser.GameObjects.Container,
    _cellId: string,
    totalDuration: number = 5000
  ): Phaser.Tweens.TweenChain {
    if (this.config.reduceMotion) {
      // Simplified version: just scale and split
      const chain = this.scene.tweens.chain({
        targets: container,
        tweens: [{
          scaleX: container.scaleX * 1.3,
          duration: totalDuration * 0.8,
          ease: 'Sine.easeInOut'
        }]
      });
      return chain;
    }

    const phaseDuration = totalDuration / 6;
    const originalScale = { x: container.scaleX, y: container.scaleY };

    // Create division line indicator
    const divisionLine = this.scene.add.graphics();
    divisionLine.setAlpha(0);
    container.add(divisionLine);

    // Create nucleus glow for interphase
    const nucleusGlow = this.createNucleusGlow(container);

    // Use tweens.chain() instead of createTimeline()
    const chain = this.scene.tweens.chain({
      tweens: [
        // Phase 1: Interphase - DNA replication glow
        {
          targets: nucleusGlow,
          alpha: { from: 0, to: 0.8 },
          scale: { from: 1, to: 1.3 },
          duration: phaseDuration,
          ease: 'Sine.easeInOut',
          onStart: () => this.scene.events.emit('division:phase', 'interphase')
        },
        // Phase 2: Prophase - Chromosomes condense
        {
          targets: container,
          scaleX: originalScale.x * 1.1,
          scaleY: originalScale.y * 1.1,
          duration: phaseDuration,
          ease: 'Sine.easeInOut',
          onStart: () => this.scene.events.emit('division:phase', 'prophase')
        },
        // Phase 3: Metaphase - Chromosomes align
        {
          targets: divisionLine,
          alpha: 0.5,
          duration: phaseDuration * 0.5,
          onStart: () => {
            divisionLine.clear();
            divisionLine.lineStyle(2, 0x805ad5, 0.7);
            divisionLine.lineBetween(0, -40, 0, 40);
            this.scene.events.emit('division:phase', 'metaphase');
          }
        },
        // Phase 4: Anaphase - Chromosomes separate
        {
          targets: container,
          scaleX: originalScale.x * 1.4,
          duration: phaseDuration,
          ease: 'Quad.easeIn',
          onStart: () => this.scene.events.emit('division:phase', 'anaphase')
        },
        // Phase 5: Telophase - Nuclear membranes reform
        {
          targets: nucleusGlow,
          alpha: 0.3,
          duration: phaseDuration * 0.5,
          onStart: () => this.scene.events.emit('division:phase', 'telophase')
        },
        // Phase 6: Cytokinesis - Cell pinches and divides
        {
          targets: container,
          scaleY: originalScale.y * 0.6,
          duration: phaseDuration,
          ease: 'Quad.easeInOut',
          onStart: () => this.scene.events.emit('division:phase', 'cytokinesis'),
          onComplete: () => {
            nucleusGlow.destroy();
            divisionLine.destroy();
            this.scene.events.emit('division:phase', 'complete');
          }
        }
      ]
    });

    this.divisionAnimations.set(_cellId, {
      tweenChain: chain,
      phase: 'interphase',
      container
    });

    return chain;
  }

  private createNucleusGlow(container: Phaser.GameObjects.Container): Phaser.GameObjects.Graphics {
    const glow = this.scene.add.graphics();
    glow.fillStyle(0x805ad5, 0.3);
    glow.fillCircle(0, 0, 20);
    glow.setAlpha(0);
    container.add(glow);
    return glow;
  }

  // ============================================================================
  // RESPONSE TO STIMULI EFFECTS
  // ============================================================================

  /**
   * Visualize cell response to a stimulus
   */
  visualizeResponse(
    container: Phaser.GameObjects.Container,
    _cellId: string,
    stimulus: StimulusType,
    direction: number = 1
  ): void {
    if (this.config.reduceMotion) {
      this.flashEffect(container, this.getStimulusColor(stimulus), 300);
      return;
    }

    const color = this.getStimulusColor(stimulus);
    const moveDistance = 30 * direction * this.config.intensity;

    // Color flash
    const flash = this.scene.add.graphics();
    flash.fillStyle(color, 0.4);
    flash.fillCircle(0, 0, 50);
    flash.setAlpha(0);
    container.add(flash);

    this.scene.tweens.add({
      targets: flash,
      alpha: 0.6,
      scale: 1.5,
      duration: 200,
      yoyo: true,
      onComplete: () => flash.destroy()
    });

    // Movement response
    this.scene.tweens.add({
      targets: container,
      x: container.x + moveDistance,
      duration: 500,
      ease: 'Quad.easeOut',
      yoyo: true,
      hold: 200
    });

    // Emit response particles
    if (stimulus === StimulusType.LIGHT) {
      this.createLightResponseParticles(container, direction);
    } else if (stimulus === StimulusType.CHEMICAL) {
      this.createChemicalResponseParticles(container, direction);
    }
  }

  private getStimulusColor(stimulus: StimulusType): number {
    const colors: Record<StimulusType, number> = {
      [StimulusType.LIGHT]: 0xf6e05e,
      [StimulusType.CHEMICAL]: 0x48bb78,
      [StimulusType.TEMPERATURE]: 0xed8936,
      [StimulusType.TOUCH]: 0x90cdf4,
      [StimulusType.FOOD]: 0x68d391,
    };
    return colors[stimulus];
  }

  private createLightResponseParticles(container: Phaser.GameObjects.Container, direction: number): void {
    for (let i = 0; i < 5; i++) {
      const ray = this.scene.add.graphics();
      ray.fillStyle(0xf6e05e, 0.6);
      ray.fillTriangle(-3, 0, 3, 0, 0, 20 * direction);
      ray.setPosition((i - 2) * 15, -40 * direction);
      ray.setAlpha(0);
      container.add(ray);

      this.scene.tweens.add({
        targets: ray,
        alpha: 0.8,
        y: ray.y + 20 * direction,
        delay: i * 50,
        duration: 300,
        yoyo: true,
        onComplete: () => ray.destroy()
      });
    }
  }

  private createChemicalResponseParticles(container: Phaser.GameObjects.Container, direction: number): void {
    for (let i = 0; i < 8; i++) {
      const particle = this.scene.add.graphics();
      particle.fillStyle(0x48bb78, 0.7);
      particle.fillCircle(0, 0, 2 + Math.random() * 2);

      const angle = Math.random() * Math.PI * 2;
      const dist = 40 + Math.random() * 20;
      particle.setPosition(Math.cos(angle) * dist * direction, Math.sin(angle) * dist);
      particle.setAlpha(0);
      container.add(particle);

      this.scene.tweens.add({
        targets: particle,
        alpha: 0.8,
        x: particle.x * 0.3,
        y: particle.y * 0.3,
        delay: i * 30,
        duration: 400,
        onComplete: () => particle.destroy()
      });
    }
  }

  // ============================================================================
  // HOMEOSTASIS EFFECTS
  // ============================================================================

  /**
   * Visualize homeostasis with an oscillating indicator
   */
  visualizeHomeostasis(
    container: Phaser.GameObjects.Container,
    _cellId: string,
    _parameter: string,
    deviation: number
  ): void {
    if (this.config.reduceMotion) {
      this.flashEffect(container, 0x4299e1, 300);
      return;
    }

    // Create balance indicator
    const indicator = this.scene.add.graphics();
    indicator.lineStyle(3, 0x4299e1, 0.8);
    indicator.lineBetween(-30, 0, 30, 0); // Center line

    // Needle showing deviation
    const needleLength = 25;
    const startAngle = (deviation / 50) * (Math.PI / 4); // Max 45 degrees
    indicator.lineStyle(4, deviation > 0 ? 0xed8936 : 0x4299e1, 1);
    indicator.lineBetween(0, 0, Math.sin(startAngle) * needleLength, -Math.cos(startAngle) * needleLength);

    indicator.setPosition(0, -50);
    indicator.setAlpha(0);
    container.add(indicator);

    // Animate needle returning to center
    const centerAngle = 0;
    this.scene.tweens.add({
      targets: { angle: startAngle },
      angle: centerAngle,
      duration: 1500,
      ease: 'Elastic.easeOut',
      onUpdate: (tween) => {
        const currentAngle = tween.getValue() ?? 0;
        indicator.clear();
        indicator.lineStyle(3, 0x4299e1, 0.8);
        indicator.lineBetween(-30, 0, 30, 0);
        indicator.lineStyle(4, Math.abs(currentAngle) > 0.1 ? 0xed8936 : 0x68d391, 1);
        indicator.lineBetween(0, 0, Math.sin(currentAngle) * needleLength, -Math.cos(currentAngle) * needleLength);
      },
      onStart: () => indicator.setAlpha(1),
      onComplete: () => {
        this.scene.tweens.add({
          targets: indicator,
          alpha: 0,
          delay: 500,
          duration: 300,
          onComplete: () => indicator.destroy()
        });
      }
    });
  }

  // ============================================================================
  // GROWTH EFFECTS
  // ============================================================================

  /**
   * Visualize cell growth
   */
  visualizeGrowth(
    container: Phaser.GameObjects.Container,
    _cellId: string,
    _fromSize: number,
    toSize: number
  ): void {
    if (this.config.reduceMotion) {
      container.setScale(toSize);
      return;
    }

    // Smooth scale increase
    this.scene.tweens.add({
      targets: container,
      scaleX: toSize,
      scaleY: toSize,
      duration: 1000,
      ease: 'Quad.easeOut'
    });

    // Growth particles/glow
    const glow = this.scene.add.graphics();
    glow.fillStyle(0x68d391, 0.3);
    glow.fillCircle(0, 0, 60);
    glow.setAlpha(0);
    glow.setBlendMode(Phaser.BlendModes.ADD);
    container.add(glow);

    this.scene.tweens.add({
      targets: glow,
      alpha: 0.5,
      scale: 1.2,
      duration: 500,
      yoyo: true,
      onComplete: () => glow.destroy()
    });
  }

  // ============================================================================
  // WASTE REMOVAL EFFECTS
  // ============================================================================

  /**
   * Visualize waste removal (excretion)
   */
  visualizeWasteRemoval(
    container: Phaser.GameObjects.Container,
    _cellId: string,
    amount: number
  ): void {
    if (this.config.reduceMotion) {
      this.flashEffect(container, 0x975a16, 300);
      return;
    }

    const particleCount = Math.ceil(amount / 5) * this.config.intensity;

    for (let i = 0; i < particleCount; i++) {
      const particle = this.scene.add.graphics();
      particle.fillStyle(0x975a16, 0.6); // Brown for waste
      particle.fillCircle(0, 0, 2 + Math.random() * 2);

      const angle = Math.random() * Math.PI * 2;
      const startDist = 30;
      particle.setPosition(Math.cos(angle) * startDist, Math.sin(angle) * startDist);
      particle.setAlpha(0);
      container.add(particle);

      // Animate waste particles moving outward
      this.scene.tweens.add({
        targets: particle,
        alpha: 0.8,
        x: Math.cos(angle) * (startDist + 40),
        y: Math.sin(angle) * (startDist + 40),
        delay: i * 50,
        duration: 600,
        ease: 'Quad.easeOut',
        onComplete: () => {
          this.scene.tweens.add({
            targets: particle,
            alpha: 0,
            duration: 200,
            onComplete: () => particle.destroy()
          });
        }
      });
    }
  }

  // ============================================================================
  // GAS EXCHANGE EFFECTS
  // ============================================================================

  /**
   * Visualize gas exchange (O2 in, CO2 out)
   */
  visualizeGasExchange(
    container: Phaser.GameObjects.Container,
    _cellId: string
  ): void {
    if (this.config.reduceMotion) {
      this.flashEffect(container, 0x90cdf4, 300);
      return;
    }

    // Oxygen particles coming in (blue)
    for (let i = 0; i < 5; i++) {
      const o2 = this.scene.add.graphics();
      o2.fillStyle(0x90cdf4, 0.8);
      o2.fillCircle(0, 0, 3);

      const angle = Math.random() * Math.PI * 2;
      const startDist = 60;
      o2.setPosition(Math.cos(angle) * startDist, Math.sin(angle) * startDist);
      container.add(o2);

      this.scene.tweens.add({
        targets: o2,
        x: Math.cos(angle) * 20,
        y: Math.sin(angle) * 20,
        alpha: 0,
        delay: i * 80,
        duration: 500,
        ease: 'Quad.easeIn',
        onComplete: () => o2.destroy()
      });
    }

    // CO2 particles going out (gray)
    for (let i = 0; i < 5; i++) {
      const co2 = this.scene.add.graphics();
      co2.fillStyle(0xa0aec0, 0.6);
      co2.fillCircle(0, 0, 3);

      const angle = Math.random() * Math.PI * 2;
      co2.setPosition(Math.cos(angle) * 20, Math.sin(angle) * 20);
      co2.setAlpha(0);
      container.add(co2);

      this.scene.tweens.add({
        targets: co2,
        x: Math.cos(angle) * 60,
        y: Math.sin(angle) * 60,
        alpha: { from: 0.8, to: 0 },
        delay: 200 + i * 80,
        duration: 500,
        ease: 'Quad.easeOut',
        onComplete: () => co2.destroy()
      });
    }
  }

  // ============================================================================
  // UTILITY METHODS
  // ============================================================================

  /**
   * Simple flash effect for reduced motion mode
   */
  private flashEffect(container: Phaser.GameObjects.Container, color: number, duration: number): void {
    const flash = this.scene.add.graphics();
    flash.fillStyle(color, 0.3);
    flash.fillCircle(0, 0, 50);
    flash.setAlpha(0);
    container.add(flash);

    this.scene.tweens.add({
      targets: flash,
      alpha: 0.6,
      duration: duration / 2,
      yoyo: true,
      onComplete: () => flash.destroy()
    });
  }

  /**
   * Clean up effects for a specific effect key
   */
  private cleanupEffects(key: string): void {
    const effects = this.activeEffects.get(key);
    if (effects) {
      effects.forEach(effect => {
        if (effect && effect.destroy) {
          effect.destroy();
        }
      });
      this.activeEffects.delete(key);
    }
  }

  /**
   * Stop all effects for a cell
   */
  stopAllEffects(cellId: string): void {
    // Clean up active effects
    for (const [key] of this.activeEffects) {
      if (key.startsWith(cellId)) {
        this.cleanupEffects(key);
      }
    }

    // Stop division animation
    const divisionAnim = this.divisionAnimations.get(cellId);
    if (divisionAnim) {
      divisionAnim.tweenChain.stop();
      this.divisionAnimations.delete(cellId);
    }
  }

  /**
   * Set reduced motion mode
   */
  setReduceMotion(enabled: boolean): void {
    this.config.reduceMotion = enabled;
  }

  /**
   * Clean up all resources
   */
  destroy(): void {
    for (const [key] of this.activeEffects) {
      this.cleanupEffects(key);
    }

    for (const anim of this.divisionAnimations.values()) {
      anim.tweenChain.stop();
    }

    for (const emitter of this.particleEmitters.values()) {
      emitter.destroy();
    }

    this.activeEffects.clear();
    this.divisionAnimations.clear();
    this.particleEmitters.clear();
  }
}
